// MissionTaskChecklistSetFieldModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import Modal from 'components/modal/Modal';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import ModalMessage from 'components/modal/message/ModalMessage';

/**
 * MissionTaskChecklistSetFieldModal
 *
 * Lightweight confirm when adding a Set Field checklist action — optional
 * requireValue gate before the checklist item can be checked at runtime.
 *
 * @param {boolean} isOpen
 * @param {function} setOpen
 * @param {object|null} [field] - Modal field entry ({ key, label })
 * @param {function} [onConfirm] - ({ field, requireValue }) => void
 * @returns {ReactNode}
 */
const MissionTaskChecklistSetFieldModal = React.memo(({
  isOpen,
  setOpen,
  field = null,
  onConfirm,
}) => {
  const isMobile = useIsMobile();
  const [requireValue, setRequireValue] = useState(false);

  const fieldLabel = String(field?.label || field?.key || 'Field').trim() || 'Field';

  useEffect(() => {
    if (!isOpen) {
      setRequireValue(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setOpen?.(false);
  };

  const handleAdd = () => {
    if (!field?.key) return;
    onConfirm?.({ field, requireValue });
    setOpen?.(false);
  };

  return (
    <Modal
      title={`Set ${fieldLabel}`}
      isOpen={isOpen}
      height="auto"
      width={isMobile ? '100%' : '420px'}
      onClose={handleClose}
    >
      <ModalForm>
        <ModalMessage
          text={`Add a "Set ${fieldLabel}" action to this checklist item.`}
        />
        <Checkbox
          label="Require a value before the checkbox can be checked."
          size="16"
          value={requireValue}
          onChange={() => setRequireValue((prev) => !prev)}
        />
      </ModalForm>

      <ModalButtons>
        <Button
          iconKey="close"
          label="Cancel"
          onClick={handleClose}
        />
        <Button
          iconKey="check"
          label="Add"
          onClick={handleAdd}
        />
      </ModalButtons>
    </Modal>
  );
});

MissionTaskChecklistSetFieldModal.displayName = 'MissionTaskChecklistSetFieldModal';

export default MissionTaskChecklistSetFieldModal;
